import React, {useState, useEffect, useRef, useCallback} from 'react';
import {useSearchParams} from 'react-router-dom';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {
  Headline,
  Button,
  Icon,
  Banner,
  Label,
  designSystemToken,
} from '@lightricks/react-design-system';
import AnalyticsService, {
  eventNames,
} from '@/services/analytics/AnalyticsService';
import {buttonNames, screenNames} from '@/lib/delta/deltaConstants.js';
import getUrlWithBrandId from '@/utils/getUrlWithBrandId';
import translate from '@/utils/translate';
import type CreatorGroupMembership from '@/types/creatorGroupMembership';
import {crmProfileUrlFor} from '@/views/creators/creator-crm/utils/offPlatformCreator';
import usePreferredCreatorGroupMemberships from '@/views/creators/lists/hooks/usePreferredCreatorGroupMemberships';
import getCreatorIdsForAnalytics from '@/views/creators/utils/getCreatorIdsForAnalytics';
import ListDrawer from '@/components/list-drawer';
import Page from '@/components/page';
import SearchInput from '@/components/search-input';
import useBrandId from '@/hooks/use-brand-id';
import useNavigation from '@/hooks/use-navigation';
import useListCreatorsStore, {
  listCreatorsActions,
} from '@/stores/list-creators/listCreatorsStore';
import styles from './CreatorLists.module.scss';
import EmptyState from './EmptyState';
import InviteExternalCreatorsModal from './InviteExternalCreatorsModal';
import ListCreatorsTable from './ListCreatorsTable';
import ListAction from './components/ListAction';
import ListCreatorsActionBar from './components/ListCreatorsActionBar';
import usePreferredCreatorGroupLabels from './hooks/usePreferredCreatorGroupLabels';

const TRANSLATION_PREFIX = 'views.list-creators';

export function showingCreatorsCountText(
  totalCreators: number | undefined
): string | null {
  if (typeof totalCreators !== 'number') {
    return null;
  }

  return translate(`${TRANSLATION_PREFIX}.showing-creators-count`, {
    count: totalCreators,
  });
}

function useSyncSelectedListsFromSearchParams() {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const selectedLists = searchParams.get('selectedLists');
    if (selectedLists) {
      listCreatorsActions.selectMultipleLists(selectedLists.split(','));
    }
  }, []);
}

function useSyncSelectedListsToSearchParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedLists = [
    ...useListCreatorsStore(
      useShallow((state) => state.selectedLists)
    ).values(),
  ].join(',');

  useEffect(() => {
    if (selectedLists) {
      searchParams.set('selectedLists', selectedLists);
    } else {
      searchParams.delete('selectedLists');
    }
    setSearchParams(searchParams);
  }, [selectedLists]);
}

function useResetOnBrandChange(brandId: string) {
  useEffect(() => {
    listCreatorsActions.reset();
  }, [brandId]);
}

function CreatorLists() {
  useSyncSelectedListsFromSearchParams();
  useSyncSelectedListsToSearchParams();

  const {navigate} = useNavigation();

  const {labels} = usePreferredCreatorGroupLabels();
  const brandId = useBrandId();
  useResetOnBrandChange(brandId);

  const {memberships, isLoading, isFetching, fetchNextPage, totalCreators} =
    usePreferredCreatorGroupMemberships();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [bannerMessage, setBannerMessage] = useState('');

  const eventSent = useRef(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);

    AnalyticsService.dispatchEvent(eventNames.BUTTON_PRESSED, {
      button_name: buttonNames.MY_CREATORS.INVITE_EXTERNAL_CREATORS,
      screen_name: screenNames.MY_CREATORS.MY_CREATORS,
      flow_id: '',
      flow_name: '',
      triggered_flow_id: '',
      triggered_flow_name: '',
      screen_presentation_id:
        AnalyticsService.getActiveScreenPresented().screen_presentation_id,
      campaign_id: '',
      creator_id: '',
      tab: '',
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleInviteSuccess = (message: string) => {
    setBannerMessage(message);
    setBannerVisible(true);
  };

  const renderEmptyState = () => {
    if (labels.length === 0 && !isLoading) {
      return (
        <EmptyState
          message={translate(`${TRANSLATION_PREFIX}.empty-state.no-lists`)}
          description={translate(
            `${TRANSLATION_PREFIX}.empty-state.no-lists-description`
          )}
          buttonText={translate(`${TRANSLATION_PREFIX}.empty-state.button`)}
          buttonLink={`/search/?brandId=${brandId}`}
        />
      );
    }
    if (memberships.length === 0 && !isLoading) {
      return (
        <EmptyState
          message={translate(`${TRANSLATION_PREFIX}.empty-state.no-creators`)}
          description={translate(
            `${TRANSLATION_PREFIX}.empty-state.no-creators-description`
          )}
          buttonText={translate(`${TRANSLATION_PREFIX}.empty-state.button`)}
          buttonLink={`/search/?brandId=${brandId}`}
        />
      );
    }
    return null;
  };

  useEffect(() => {
    const screenPresentationId = uuid();
    if (memberships.length > 0 && !eventSent.current) {
      AnalyticsService.dispatchEvent(eventNames.MY_CREATORS_RESULTS_PRESENTED, {
        screen_presentation_id: screenPresentationId,
        creator_ids: getCreatorIdsForAnalytics(memberships),
        result_source: 'my_creators',
      });

      eventSent.current = true;
    }
  }, [memberships]);

  const handleRowClick = useCallback(
    (row: CreatorGroupMembership) => {
      const profileId = row.profile?.id;
      if (!profileId) {
        return;
      }

      const profileUrl = crmProfileUrlFor({
        id: profileId,
        source: row.source,
      });
      if (!profileUrl) {
        return;
      }

      navigate(getUrlWithBrandId(profileUrl));
    },
    [navigate]
  );

  return (
    <Page
      id="creator-lists"
      className={styles.container}
      childrenClassName={styles.content}
    >
      {bannerVisible && (
        <Banner
          isOpen={bannerVisible}
          title={bannerMessage}
          severity="success"
          mode="light"
          onActionClick={() => setBannerVisible(false)}
          icon={
            <Icon
              size="medium"
              appearance="brand"
              name="Actions-Patch-Accept-Line"
            />
          }
          className={styles.banner}
        />
      )}
      <div className={styles.main}>
        <ListDrawer labels={labels} totalEntries={totalCreators} />
        <div className={styles.mainColumn}>
          <Headline size="lg" className={styles.pageTitle}>
            {translate(`${TRANSLATION_PREFIX}.title`)}
          </Headline>
          <div className={styles.actionRow}>
            <div className={styles.searchWrapper}>
              <SearchInput
                placeholder={translate(
                  `${TRANSLATION_PREFIX}.search.placeholder`
                )}
                onSubmit={(val) => {
                  listCreatorsActions.setTableFilterValue(val);
                }}
              />
            </div>
            <div className={styles.actionRowRight}>
              <Button
                appearance="neutral"
                mode="plain"
                size="small"
                icon={
                  <Icon
                    size="small"
                    appearance="neutral"
                    name="Navigation-Mail"
                  />
                }
                onClick={handleOpenModal}
              >
                {translate(
                  `${TRANSLATION_PREFIX}.button.invite-external-creators`
                )}
              </Button>
              <span className={styles.separator} aria-hidden />
              <Label
                size="sm"
                color={designSystemToken('semantic.fg.secondary')}
                style={{whiteSpace: 'nowrap'}}
              >
                {showingCreatorsCountText(totalCreators)}
              </Label>
            </div>
          </div>
          {renderEmptyState() || (
            <ListCreatorsActionBar>
              <ListCreatorsTable
                data={memberships}
                isLoading={isLoading}
                isFetching={isFetching}
                fetchNextPage={fetchNextPage}
                onRowClick={handleRowClick}
              />
            </ListCreatorsActionBar>
          )}
        </div>
      </div>
      <ListAction />
      <InviteExternalCreatorsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleInviteSuccess}
        listOptions={labels}
      />
    </Page>
  );
}

export default CreatorLists;
